/* eslint-disable @typescript-eslint/no-explicit-any */
import clone from "lodash/clone";
import each from "lodash/each";
import map from "lodash/map";
import values from "lodash/values";
import { VISIBILITY_MAP as VISIBILITY_MAP_BASE } from "./enums";
import { t, useTranslation } from "./utils";

export * as common from "./common";
export * as enums from "./enums";
export * as enumsTypes from "./enums/enums-types";
export * as financials from "./financials";
export * as links from "./links/index";
export * as postMessages from "./post-messages";
export * as purchasing from "./purchasing";

useTranslation("constants");

// Invoice type semantic tokens - used with Chakra's color tokens
const invoice_sale_textColor = "green.900";
const invoice_sale_bgColor = "green.200";
const invoice_refund_textColor = "red.900";
const invoice_refund_bgColor = "red.200";
const invoice_exchange_textColor = "purple.900";
const invoice_exchange_bgColor = "purple.200";
const invoice_transfer_textColor = "blue.900";
const invoice_transfer_bgColor = "blue.200";
const invoice_void_bgColor = "bg.muted";
const invoice_void_textColor = "fg.muted";
const invoice_other_textColor = "blue.900";
const invoice_other_bgColor = "blue.200";

export const INVOICE_TYPE_OPTIONS = {
  INVOICE_TYPE_SALE: {
    id: 101,
    name: t("Sale"),
    publicName: t("Purchase"),
    bgColor: invoice_sale_bgColor,
    textColor: invoice_sale_textColor,
  },
  INVOICE_TYPE_PAYMENT_PLAN: {
    id: 102,
    name: t("Payment Plan"),
    publicName: t("Payment Plan"),
    bgColor: invoice_other_bgColor,
    textColor: invoice_other_textColor,
  },
  INVOICE_TYPE_REFUND: {
    id: 201,
    name: t("Refund"),
    publicName: t("Refund"),
    bgColor: invoice_refund_bgColor,
    textColor: invoice_refund_textColor,
  },
  INVOICE_TYPE_VOID: {
    id: 701,
    name: t("Void"),
    publicName: t("Void"),
    bgColor: invoice_void_bgColor,
    textColor: invoice_void_textColor,
  },
  INVOICE_TYPE_PAYMENT_DUE: {
    id: 301,
    name: t("Service Fee"),
    publicName: t("Service Fee"),
    bgColor: invoice_other_bgColor,
    textColor: invoice_other_textColor,
  },
  INVOICE_TYPE_ATTRIBUTED_ADVANCE: {
    id: 304,
    name: t("Attributed Advance"),
    publicName: t("Attributed Advance"),
    bgColor: invoice_other_bgColor,
    textColor: invoice_other_textColor,
  },
  INVOICE_TYPE_PAYMENT_RECEIVED: {
    id: 401,
    name: t("Payout"),
    publicName: t("Payout"),
    bgColor: invoice_other_bgColor,
    textColor: invoice_other_textColor,
  },
  INVOICE_TYPE_PAYMENT_COMPLETED: {
    id: 403,
    name: t("Payment Plan Completed"),
    publicName: t("Payment Plan Completed"),
    bgColor: invoice_other_bgColor,
    textColor: invoice_other_textColor,
  },
  INVOICE_TYPE_DONATION_PAYOUT: {
    id: 404,
    name: t("Donation"),
    publicName: t("Donation"),
    bgColor: invoice_other_bgColor,
    textColor: invoice_other_textColor,
  },
  INVOICE_TYPE_PAYOUT: {
    id: 402,
    name: t("Invoice"),
    publicName: t("Invoice"),
    bgColor: invoice_other_bgColor,
    textColor: invoice_other_textColor,
  },
  INVOICE_TYPE_TICKET_TRANSFER: {
    id: 501,
    name: t("Transfer"),
    publicName: t("Transfer"),
    bgColor: invoice_transfer_bgColor,
    textColor: invoice_transfer_textColor,
  },
  INVOICE_TYPE_DISPUTE: {
    id: 601,
    name: t("Dispute"),
    publicName: t("Dispute"),
    bgColor: invoice_other_bgColor,
    textColor: invoice_other_textColor,
  },
  INVOICE_TYPE_CREDIT_MEMO: {
    id: 302,
    name: t("Credit Memo"),
    publicName: t("Credit Memo"),
    bgColor: invoice_other_bgColor,
    textColor: invoice_other_textColor,
  },
  INVOICE_TYPE_ADVANCE: {
    id: 303,
    name: t("Advance"),
    publicName: t("Advance"),
    bgColor: invoice_other_bgColor,
    textColor: invoice_other_textColor,
  },
  INVOICE_TYPE_VENUE_CREDIT: {
    id: 802,
    name: t("Account Credit Refund"),
    publicName: t("Account Credit Refund"),
    bgColor: invoice_other_bgColor,
    textColor: invoice_other_textColor,
  },
  INVOICE_TYPE_EXCHANGE: {
    id: 803,
    name: t("Exchanged"),
    publicName: t("Exchanged"),
    bgColor: invoice_exchange_bgColor,
    textColor: invoice_exchange_textColor,
  },
  INVOICE_TYPE_EXCHANGE_IN_PROGRESS: {
    id: 804,
    name: t("Exchange In Progress"),
    publicName: t("Exchange In Progress"),
    bgColor: invoice_other_bgColor,
    textColor: invoice_other_textColor,
  },
  INVOICE_TYPE_EXCHANGE_ADJUSTMENT: {
    id: 805,
    name: t("Exchange Adjustment"),
    publicName: t("Exchange Adjustment"),
    bgColor: invoice_other_bgColor,
    textColor: invoice_other_textColor,
  },
  INVOICE_TYPE_EXCHANGE_REFUND_CORRECTION: {
    id: 806,
    name: t("Exchange Refund Correction"),
    publicName: t("Exchange Refund Correction"),
    bgColor: invoice_other_bgColor,
    textColor: invoice_other_textColor,
  },
} as const;

export const INVOICE_TYPE_OPTIONS_BY_LIST = values(INVOICE_TYPE_OPTIONS);

export type InvoiceTypeOptionsType =
  (typeof INVOICE_TYPE_OPTIONS)[keyof typeof INVOICE_TYPE_OPTIONS];

export type InvoiceTypeOptionsIDType = InvoiceTypeOptionsType["id"];

export const EVENT_TAGS = {
  FESTIVALS: {
    name: t("Festivals"),
  },
  COMMUNITY: {
    name: t("Community"),
  },
  ROCK_AND_ROLL: {
    name: t("Rock & Roll"),
  },
  RAP_AND_HIP_HOP: {
    name: t("Rap & Hip Hop"),
  },
  ELECTRONICS_AND_DJ: {
    name: t("Electronics & DJ"),
  },
  SPORTS: {
    name: t("Sports"),
  },
  FOOD_AND_BEVERAGE: {
    name: t("Food & Beverage"),
  },
  COUNTRY_AND_FOLK: {
    name: t("Country & Folk"),
  },
  BUSINESS: {
    name: t("Business"),
  },
  HEALTH: {
    name: t("Health"),
  },
  ARTS: {
    name: t("Arts"),
  },
  CHARITY_AND_CAUSES: {
    name: t("Charity & Causes"),
  },
  FAMILY_AND_EDUCATION: {
    name: t("Family & Education"),
  },
  MUSIC: {
    name: t("Music"),
  },
  HOBBIES: {
    name: t("Hobbies"),
  },
  SPIRITUALITY: {
    name: t("Spirituality"),
  },
  SCIENCE_AND_TECH: {
    name: t("Science & Tech"),
  },
  HOME_AND_LIFESTYLE: {
    name: t("Home & Lifestyle"),
  },
  TRAVEL_AND_OUTDOORS: {
    name: t("Travel & Outdoors"),
  },
  FILM_AND_MEDIA: {
    name: t("Film & Media"),
  },
  FASHION: {
    name: t("Fashion"),
  },
  GOVERNMENT: {
    name: t("Government"),
  },
  HOLIDAY: {
    name: t("Holiday"),
  },
  AUTO_BOAT_AND_AIR: {
    name: t("Auto, Boat & Air"),
  },
  STAMPEDE: {
    name: t("Stampede"),
  },
  COMEDY: {
    name: t("Comedy"),
  },
  OTHER: {
    name: t("Other"),
  },
} as const;

export const AFTERPAY_MIN = 1;
export const AFTERPAY_MAX = 2000;
export const AFFIRM_MIN = 50;
export const AFFIRM_MAX = 20000;
export const PAYMENT_TYPE_OPTIONS_MAP = {
  CASH: {
    id: 1,
    name: t("Cash"),
    bulkScanPermitted: true,
  },
  CREDIT: {
    id: 2,
    name: t("Card"),
    bulkScanPermitted: false,
  },
  INTERAC: {
    id: 3,
    name: t("Interac Online"),
    bulkScanPermitted: false,
  },
  COMPLIMENTARY: {
    id: 4,
    name: t("Complimentary"),
    bulkScanPermitted: true,
  },
  FREE: {
    id: 5,
    name: t("Free"),
    bulkScanPermitted: false,
  },
  BUNDLED: {
    id: 6,
    name: t("Bundled"),
    bulkScanPermitted: false,
  },
  TRANSFERRED: {
    id: 7,
    name: t("Transferred"),
    bulkScanPermitted: false,
  },
  OTHER: {
    id: 9,
    name: t("Other"),
    bulkScanPermitted: false,
  },
  PAYMENT_PLAN: {
    id: 10,
    name: t("Payment Plan"),
    bulkScanPermitted: false,
  },
  USER_CREDIT: {
    id: 11,
    name: t("User Credit"),
    bulkScanPermitted: false,
  },
  EXCHANGE_CREDIT: {
    id: 12,
    name: t("Exchange Credit"),
    bulkScanPermitted: false,
  },
  AFTERPAY: {
    id: 13,
    name: "Afterpay",
    bulkScanPermitted: false,
    minAmount: AFTERPAY_MIN,
    maxAmount: AFTERPAY_MAX,
  },
  AFFIRM: {
    id: 14,
    name: "Affirm",
    bulkScanPermitted: false,
    minAmount: AFFIRM_MIN,
    maxAmount: AFFIRM_MAX,
  },
  AUTO_GENERATED: {
    id: 15,
    name: "Auto Generated",
    bulkScanPermitted: false,
  },
} as const;

export type PaymentTypeOptionsType =
  (typeof PAYMENT_TYPE_OPTIONS_MAP)[keyof typeof PAYMENT_TYPE_OPTIONS_MAP];

export type PaymentTypeOptionsIDType = PaymentTypeOptionsType["id"];

export const TICKET_STATUS_OPTIONS = {
  STATUS_IN_BASKET: {
    id: 1,
    includedInTotal: false,
    name: t("In cart"),
    labelClass: "label-gray",
    textColor: "text-gray",
    transferDisabled: true,
  },
  STATUS_PAYED: {
    id: 2,
    includedInTotal: true,
    name: t("Issued"),
    labelClass: "label-green",
    textColor: "text-green",
    transferDisabled: false,
  },
  STATUS_RESOLD: {
    id: 3,
    includedInTotal: false,
    name: t("Resold"),
    labelClass: "label-gray",
    textColor: "text-gray",
    transferDisabled: true,
  },
  STATUS_USED: {
    id: 4,
    includedInTotal: true,
    name: t("Scanned"),
    labelClass: "label-danger",
    textColor: "text-danger",
    transferDisabled: true,
  },
  STATUS_REFUNDED: {
    id: 5,
    includedInTotal: false,
    name: t("Refunded"),
    labelClass: "label-danger",
    textColor: "text-danger",
    transferDisabled: true,
  },
  STATUS_VOIDED: {
    id: 6,
    includedInTotal: false,
    name: t("Voided"),
    labelClass: "label-danger",
    textColor: "text-danger",
    transferDisabled: true,
  },
  STATUS_TRANSFERRED: {
    id: 7,
    includedInTotal: false,
    name: t("Transferred"),
    labelClass: "label-primary",
    textColor: "text-primary",
    transferDisabled: true,
  },
  STATUS_REFUND_IN_PROGRESS: {
    id: 12,
    includedInTotal: false,
    name: t("Refund in progress"),
    labelClass: "label-warning",
    textColor: "text-warning",
    transferDisabled: true,
  },
  STATUS_TRANSFER_RETURNED: {
    id: 13,
    includedInTotal: false,
    name: t("Transfer Returned"),
    labelClass: "label-warning",
    textColor: "text-warning",
    transferDisabled: true,
  },
  STATUS_WAITLISTED: {
    id: 15,
    includedInTotal: false,
    name: t("Waitlisted"),
    labelClass: "label-warning",
    textColor: "text-warning",
    transferDisabled: true,
  },
  STATUS_RESALE_PENDING: {
    id: 16,
    includedInTotal: true,
    name: t("Resale Pending"),
    labelClass: "label-warning",
    textColor: "text-warning",
    transferDisabled: true,
  },
} as const;

export type TicketStatusOptionsIdType =
  (typeof TICKET_STATUS_OPTIONS)[keyof typeof TICKET_STATUS_OPTIONS]["id"];

export const ITEM_STATUS_OPTIONS = {
  PENDING: {
    id: 1,
    name: "Pending",
  },
  PAYED: {
    id: 2,
    name: "Issued",
  },
  RESOLD: {
    id: 3,
    name: "Resold",
  },
  USED: {
    id: 4,
    name: "Scanned",
  },
  REFUNDED: {
    id: 5,
    name: "Refunded",
  },
  VOIDED: {
    id: 6,
    name: "Voided",
  },
  TRANSFERRED: {
    id: 7,
    name: "Transferred",
  },
  EXPIRED: {
    id: 8,
    name: "Expired",
  },
  SUSPECTED_FRAUD: {
    id: 9,
    name: "Suspected Fraud",
  },
  ON_HOLD: {
    id: 10,
    name: "Hold",
  },
  SELECTION_REQUIRED: {
    id: 11,
    name: "Selection Required",
  },
  REFUND_IN_PROGRESS: {
    id: 12,
    name: "Refund In Progress",
  },
  WAITLISTED: {
    id: 14,
    name: "Waitlisted",
  },
} as const;

export const EXCHANGE_TICKETS_FEE_TYPES = {
  CUSTOMER_PAYS: {
    id: "customer_pays",
    name: "Customer Pays",
  },
  ORGANIZER_PAYS: {
    id: "organizer_pays",
    name: "Organizer Pays",
  },
  NO_FEES: {
    id: "no_fees",
    name: "No Fees",
  },
  CUSTOMER_PAYS_ALL_FEES: {
    id: "customer_pays_all_fees",
    name: "Customer Pays - All Fees",
  },
} as const;

export const INVOICE_ITEM_TYPE_OPTIONS = {
  INVOICE_ITEM_TYPE_TICKET: {
    id: "ticket",
    name: "Ticket",
    icon: "icon-ticket fa fa-ticket",
  },
  INVOICE_ITEM_TYPE_PRODUCT: {
    id: "product",
    name: "Product",
    icon: "icon-shopping-cart fa fa-shopping-cart",
  },
  INVOICE_ITEM_TYPE_REFUND: {
    id: "refund",
    name: "Refund",
    icon: "icon-remove fa fa-remove",
  },
  INVOICE_ITEM_TYPE_TRANSFER: {
    id: "transfer",
    name: "Transfer",
    icon: "icon-share fa fa-share",
  },
  INVOICE_ITEM_TYPE_SETTLEMENT: {
    id: "settlement",
    name: "Settlement",
    icon: "icon-exchange fa fa-exchange",
  },
  INVOICE_ITEM_TYPE_FEE: {
    id: "fee",
    name: "Fee",
    icon: "icon-tag fa fa-tag",
  },
  INVOICE_ITEM_TYPE_TIP: {
    id: "tip",
    name: "Fee",
    icon: "icon-heart-empty fa fa-heart-o",
  },
  INVOICE_ITEM_TYPE_DONATION: {
    id: "donation",
    name: "Donation",
    icon: "icon-heart fa fa-heart",
  },
  INVOICE_ITEM_TYPE_USER_CREDIT: {
    id: "user_credit",
    name: "User Credit",
    icon: "icon-money fa fa-money",
  },
  INVOICE_ITEM_TYPE_VENUE_CREDIT: {
    id: "venue_credit",
    name: "User Venue Credit",
    icon: "icon-money fa fa-money",
  },
  INVOICE_ITEM_TYPE_REFERRAL_CREDIT: {
    id: "referral_credit",
    name: "Referral Credit",
    icon: "icon-money fa fa-money",
  },
  INVOICE_ITEM_TYPE_MEMBERSHIP: {
    id: "membership",
    name: "Membership",
    icon: "icon-id-card-o fa fa-id-card-o",
  },
  INVOICE_ITEM_TYPE_PAYMENT_PLAN: {
    id: "payment_plan",
    name: "Payment Plan",
    icon: "icon-credit-card fa fa-credit-card",
  },
  INVOICE_ITEM_TYPE_REVENUE_REALIZATION_ADJUSTMENT: {
    id: "revenue_realization_adjustment",
    name: "Revenue Realization Adjustment",
    icon: "icon-adjust fa fa-adjust",
  },
  INVOICE_ITEM_TYPE_REVENUE_REALIZATION_NEGATION: {
    id: "revenue_realization_negation",
    name: "Revenue Realization Negation",
    icon: "icon-remove fa fa-remove",
  },
  INVOICE_ITEM_TYPE_GIFT_CARD: {
    id: "gift_card",
    name: "Gift Card",
    icon: "icon-gift fa fa-gift",
  },
  INVOICE_ITEM_TYPE_PROTECTION: {
    id: "protection",
    name: "Protection",
    icon: "icon-shield fa fa-shield",
  },
} as const;

export type InvoiceItemTypeOptions =
  (typeof INVOICE_ITEM_TYPE_OPTIONS)[keyof typeof INVOICE_ITEM_TYPE_OPTIONS];

export type InvoiceItemType = InvoiceItemTypeOptions["id"];

export const CELERY_STATUSES = {
  PENDING: {
    id: "PENDING",
    name: "Pending",
  },
  SUCCESS: {
    id: "SUCCESS",
    name: "Success",
  },
  FAILURE: {
    id: "FAILURE",
    name: "Failure",
  },
  RETRY: {
    id: "RETRY",
    name: "Retry",
  },
};

export type CeleryStatusesMapType =
  (typeof CELERY_STATUSES)[keyof typeof CELERY_STATUSES];

export type CeleryStatusesMapIdType = CeleryStatusesMapType["id"];

export const ASSIGNED_SEATING = {
  // !IMPORTANT
  // If you are changing grid width and other seating constants it may impact how existent maps will
  // render. You may need to create a migration or script to deal with a move to new values
  // Look at misc/scripts/internal/change_assigned_seating_size.py to update existing maps.
  OPTIONS: {
    // will enable pan and scroll caching when there are many seats
    ENABLE_SCROLL_AND_PAN_CACHING: false,
    CACHE_PIXEL_RATIO: 2, // cache a 2x scaled image
    SHOW_SEATS_AT_SCALE: 0.8,
    SEATS_TO_CACHE: 400,
    MAX_SCALE: 2,
    MIN_SCALE: 0.1,
    TOOL_TIP_SCALE_FACTOR: 1.6, // increase number to make tooltip larger, decrease to make tooltip smaller
    SEAT_SIZE: 6, // default to initialize with
    SEAT_SPACING: 4, // default to initialize with
    SMALL_SEAT_SCALE_THRESHOLD: 3, // seat size threshold to handle the map scaling differently
    MAX_COUNT_TO_DISPLAY_ALL_SEATS: 4500, // do not allow any maps to display all seats at this number or over
    MAX_COUNT_TO_DISPLAY_SEATS_ON_ANDROID: 400, // do not allow any maps on android to display all seats at this number or over
  },
  EDITOR: {
    SEAT: {
      STYLE: {
        selectedStrokeWidth: 0.05,
      },
    },
    ANCHOR: {
      ANCHOR_RADIUS: 5,
      STYLE: {
        colorFill: "#F44336",
        colorStroke: "#212121",
      },
    },
    GRID: {
      GRID_WIDTH: 7,
    },
  },
  SEGMENT: {
    ID: "section",
    NAME: "Section",
    DEFAULT_SIZE: 119,
    STYLE: {
      AVAILABLE: {
        name: "Available",
        colorFill: "#328C94",
        hoverFill: "#14595F",
        helpText: "Section is available for purchase.",
      },
      UNAVAILABLE: {
        name: "Unavailable",
        colorFill: "#CECECE",
        hoverFill: "#CECECE",
        helpText: "Section is unavailable for purchase.",
      },
      SOLD_OUT: {
        name: "Sold Out",
        colorFill: "#D1D5DB",
        hoverFill: "#D1D5DB",
        helpText: "Section is sold out.",
      },
    },
  },
  SEAT: {
    NAME: "Seat",
    ID: "seat",
    RADIUS: 6,
    STYLE: {
      UNASSIGNED: {
        name: "Unassigned",
        colorFill: "#9E9E9E",
        strokeWidth: 1,
        helpText: "Seat does not have ticket type assigned.",
      },
      UNAVAILABLE: {
        name: "Unavailable",
        colorFill: "#CECECE",
        strokeWidth: 1,
        helpText: "Seat has been sold or blocked from purchase.",
      },
      CHOSEN: {
        name: "Selected",
        colorFill: "#FFEB3B",
        colorStroke: "#F57F17", // keep this colorStroke value for the checkmark coloration
        strokeWidth: 1,
        helpText: "Seat is currently in your basket.",
      },
      AVAILABLE: {
        name: "Available",
        attendanceModeName: "Sold or Held",
        colorFill: "#4CAF50",
        strokeWidth: 1,
        helpText: "Seat is available for purchase.",
      },
      ACCESSIBLE: {
        name: "Accessible",
        helpText: "Seat is designated with accessibility standards.",
        seatLabel: "A",
      },
      LIMITED_VISIBILITY: {
        name: "Limited Visibility",
        helpText: "Seat has limited visibility.",
        seatLabel: "L",
      },
      RESALE: {
        name: "Resale",
        helpText: "Seat has an official resale ticket.",
        seatLabel: "R",
      },
      EXCHANGE: {
        name: "Exchange",
        colorFill: "#f7a55c",
        colorStroke: "#FFEB3B", // for icon shown on seat
        strokeWidth: 1,
        helpText: "Seat has been exchanged.",
      },
      HOLD: {
        name: "Hold",
        helpText: "Seat is on hold.",
        seatLabel: "H",
      },
      BEST_AVAILABLE_CHOSEN: {
        name: "Best Available",
        colorFill: "#60A5FA",
        colorStroke: "#1E40AF",
        strokeWidth: 2,
        opacity: 1,
      },
      BEST_AVAILABLE_HOVERED: {
        name: "Best Available Hovered",
        colorFill: "#1D4ED8",
        colorStroke: "#1E40AF",
        strokeWidth: 2,
        opacity: 1,
      },
      BEST_AVAILABLE_GA_TEXT: {
        name: "Best Available Hovered",
        colorFill: "white",
        colorStroke: "white",
        strokeWidth: 2,
        opacity: 1,
      },
    },
  },
  GENERAL_ADMISSION: {
    ID: "general_admission",
    UNASSIGNED: {
      name: "Unassigned",
      colorFill: "#9E9E9E",
      colorStroke: "#212121",
      strokeWidth: 1,
      helpText: "Location does not have ticket type assigned.",
    },
    UNAVAILABLE: {
      name: "Unavailable",
      colorFill: "#CECECE",
      colorStroke: "#B3B3B3",
      strokeWidth: 1,
      helpText: "Location has been sold out or is blocked from purchase.",
    },
    CHOSEN: {
      name: "Selected",
      colorFill: "#FFEB3B",
      colorStroke: "#F57F17",
      strokeWidth: 1,
      helpText: "Location has tickets currently in your basket.",
    },
    AVAILABLE: {
      name: "Available",
      colorFill: "#4CAF50",
      colorStroke: "#1B5E20",
      strokeWidth: 1,
      helpText: "Location tickets are available for purchase.",
    },
  },
  SQUARE_TABLE: {
    NAME: "Square Table",
    PADDING: 3,
    MAX_CAPACITY: 50,
    STYLE: {
      colorFill: "#f3f4f6",
      colorStroke: "#4CAF50",
      strokeWidth: 1,
      labelColor: "#11142d",
    },
  },
  CIRCLE_TABLE: {
    NAME: "Circle Table",
    MAX_CAPACITY: 10,
    STYLE: {
      colorFill: "#f3f4f6",
      colorStroke: "#4CAF50",
      strokeWidth: 1,
      labelColor: "#11142d",
    },
  },
  SEMI_CIRCLE_TABLE: {
    NAME: "Semi Circle Table",
    MAX_CAPACITY: 10,
    STYLE: {
      colorFill: "#f3f4f6",
      colorStroke: "#4CAF50",
      strokeWidth: 1,
      labelColor: "#11142d",
    },
  },
  ROW: {
    NAME: "Row",
    MAX_CAPACITY: 500,
  },
  TICKET_TYPE_COLORS: {
    GREEN: {
      colorFill: "#4CAF50",
    },
    BLUE: {
      colorFill: "#03A9F4",
    },
    PINK: {
      colorFill: "#E91E63",
    },
    ORANGE: {
      colorFill: "#FF9800",
    },
    PURPLE: {
      colorFill: "#9C27B0",
    },
    CYAN: {
      colorFill: "#0091A4",
    },
    BLUE_GREY: {
      colorFill: "#607D8B",
    },
    RED: {
      colorFill: "#F44336",
    },
    INDIGO: {
      colorFill: "#3F51B5",
    },
    OLIVE: {
      colorFill: "#6b8e23",
    },
    MUSTARD: {
      colorFill: "#9d8319",
    },
    LILAC: {
      colorFill: "#bf55ec",
    },
    BROWN: {
      colorFill: "#9f6b3f",
    },
    CORAL: {
      colorFill: "#d24d57",
    },
  },
  // These sections should not contain seats or locations
  FOCAL_SECTIONS: {
    STAGE: {
      ID: "STAGE",
    },
    SCREEN: {
      ID: "SCREEN",
    },
  },
};

export const ASSIGNED_SEATING_MODES = {
  EDIT_LAYOUT: {
    id: 1,
  },
  EDIT_SEAT_TICKETS: {
    id: 2,
  },
  EVENT_DETAIL_VIEW: {
    id: 3,
  },
  EVENT_BOX_OFFICE_VIEW: {
    id: 4,
  },
  EVENT_PRINT_MAP_VIEW: {
    id: 5,
  },
  GROUP_SALES: {
    id: 6,
  },
  ATTENDANCE: {
    id: 7,
  },
  MODIFY_SEAT_SELECTION: {
    id: 8,
  },
};

export type AssignedSeatingModesType =
  (typeof ASSIGNED_SEATING_MODES)[keyof typeof ASSIGNED_SEATING_MODES];

export type AssignedSeatingModesIdType = AssignedSeatingModesType["id"];

export const DYNAMIC_HOLD_ITEM_TYPE_OPTIONS = {
  DYNAMIC_HOLD_ITEM_TYPE_EVENT: {
    id: "event",
    name: "Event",
  },
  DYNAMIC_HOLD_ITEM_TYPE_PRODUCT: {
    id: "product",
    name: "Product",
  },
  DYNAMIC_HOLD_ITEM_TYPE_MEMBERSHIP: {
    id: "membership",
    name: "Membership",
  },
};

export type DynamicHoldItemTypeOptionsType =
  (typeof DYNAMIC_HOLD_ITEM_TYPE_OPTIONS)[keyof typeof DYNAMIC_HOLD_ITEM_TYPE_OPTIONS];

export type DynamicHoldItemTypeOptionsIdType =
  DynamicHoldItemTypeOptionsType["id"];

// threshold for displaying quantity dropdown vs quantity select buttons
export const DISPLAY_QUANTITY_DROPDOWN_THRESHOLD = 8;

export enum StatusBadge {
  success = "success",
  warning = "warning",
  error = "error",
  info = "info",
  neutral = "neutral",
}

export const TRANSFER_STATES = {
  PENDING: {
    id: "pending",
    name: "In Progress",
    statusBadge: StatusBadge.warning,
  },
  CANCELLED: {
    id: "cancelled",
    name: "Cancelled",
    statusBadge: StatusBadge.error,
  },
  COMPLETE: {
    id: "complete",
    name: "Complete",
    statusBadge: StatusBadge.success,
  },
  RETURNED: {
    id: "returned",
    name: "Returned",
    statusBadge: StatusBadge.info,
  },
};

export type TransferStatesType =
  (typeof TRANSFER_STATES)[keyof typeof TRANSFER_STATES];

export type TransferStatesIdType = TransferStatesType["id"];
export type TransferState = "pending" | "cancelled" | "complete" | "returned";

export const DELIVERY_TYPE_OPTIONS = {
  TYPE_PRINT: {
    id: "print",
    label: "Print",
  },
  TYPE_EMAIL: {
    id: "email",
    label: "Email",
  },
  TYPE_CARD: {
    id: "card",
    label: "Scan Card",
  },
  TYPE_NONE: {
    id: "none",
    label: "None",
  },
};

export type DeliveryTypeOptionsType =
  (typeof DELIVERY_TYPE_OPTIONS)[keyof typeof DELIVERY_TYPE_OPTIONS];

export type DeliveryTypeOptionsIdType = DeliveryTypeOptionsType["id"];

export const INVOICE_DETAILS_DATE_TIME_FORMAT = "Do MMM, YYYY | h:mm A z";

export const GIFT_CARD_TERMS_AND_CONDITIONS = t(
  "Card balance does not expire. No fees apply to purchase or to activate this Card. Redeemable towards the purchase of eligible goods and services on the Showpass website and app, specific only to which organization the card was issued from. Use requires a Showpass account. Card is non-reloadable and, except where required by law, cannot be redeemed for cash, refunded, returned, or resold. Issuer is not responsible for lost, damaged, or stolen cards, or for unauthorized use. Obtain Card information and balance by signing in to your Showpass account. Issued by Showpass. Terms apply, see "
);

/**
 * Converts constants dict identified by name into a dict
 * identified by ID
 *
 * {MY_KEY: {id: 1, ...}, ...} => {1: {key: 'MY_KEY', ...}, ...}
 * @param dict the constants dict to convert
 * @param opts additional options
 * @returns {Object}
 */

type Options = Record<string, string>;

type Dictionary<ValueType> = Record<string, ValueType>;

type Value<ValueType> = ValueType & { key: string };

type Result<ValueType> = Record<string, Value<ValueType>>;

export const optionsByPK = <ValueType>(
  dict: Dictionary<ValueType>,
  opts?: Options
): Result<ValueType> => {
  const byID = {};

  const options = {
    pkField: "id",
    keyField: "key",
    ...opts,
  };

  each(dict, (obj, key) => {
    const value = clone(obj);
    value[options.keyField] = key;

    const id: string = value[options.pkField];
    byID[id] = value;
  });

  return byID;
};

/**
 * Converts dict options into a list.
 *
 * {MY_KEY: {id: 1, ...}, ...} => [{id: 1, ...}, ...]
 * @param dict the constants dict to convert
 * @returns {Array}
 */
export const optionsAsList = <ValueType>(
  dict: Dictionary<ValueType>
): ValueType[] => map(dict, (obj) => obj);

export const VISIBILITY_OPTIONS_COLORS = {
  [VISIBILITY_MAP_BASE.VISIBILITY_PUBLIC.key]: "green",
  [VISIBILITY_MAP_BASE.VISIBILITY_HIDDEN.key]: "yellow",
  [VISIBILITY_MAP_BASE.VISIBILITY_SELLERS_ONLY.key]: "blue",
  [VISIBILITY_MAP_BASE.VISIBILITY_PUBLIC_ONLY.key]: "purple",
};

export type VisibilityMapOptionsType = {
  key: keyof typeof VISIBILITY_MAP_BASE;
  id: number;
  name: string;
  color: string;
};
export type VisibilityMapType = {
  [key in keyof typeof VISIBILITY_MAP_BASE]: VisibilityMapOptionsType;
};

export const VISIBILITY_MAP: VisibilityMapType = Object.values(
  VISIBILITY_MAP_BASE
).reduce(
  (finalList, currentOption) => {
    return {
      ...finalList,
      [currentOption.key]: {
        ...currentOption,
        color: VISIBILITY_OPTIONS_COLORS[currentOption.key],
      },
    };
  },
  {
    [VISIBILITY_MAP_BASE.VISIBILITY_PUBLIC.key]: {
      id: VISIBILITY_MAP_BASE.VISIBILITY_PUBLIC.id,
      key: VISIBILITY_MAP_BASE.VISIBILITY_PUBLIC.key,
      name: VISIBILITY_MAP_BASE.VISIBILITY_PUBLIC.name,
      color:
        VISIBILITY_OPTIONS_COLORS[VISIBILITY_MAP_BASE.VISIBILITY_PUBLIC.key],
    },
  }
) as VisibilityMapType;

export const UNIX_PRINT_OPTIONS = [
  "-o fit-to-page",
  "-o media=Custom.140x51mm",
  "-o landscape",
];
